import { useState } from 'react'
import type {
  ReturnsCalculatedSummary,
  ReturnsTrackingInfoResponseRefundInfo
} from '@itsrever/rever-api'
import { ReturnsTrackingInfoResponseRefundInfoStatusEnum } from '@itsrever/rever-api'
import { Trans, useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { Tooltip } from '@heroui/react'
import { useAppSelector } from '@/redux/hooks'
import { getDate } from '@/utils'
import { InfoCircleIcon } from '@/ui/icons/info-circle'
import { GiftIcon } from '@/ui/icons/gift'
import { CopyIcon } from '@/ui/icons/copy'
import { CheckIcon } from '@/ui/icons/check'

interface RefundSummaryProps {
  summary: ReturnsCalculatedSummary
  refundInfo: ReturnsTrackingInfoResponseRefundInfo
}

export const RefundSummary = ({ summary, refundInfo }: RefundSummaryProps) => {
  const { t, i18n } = useTranslation()

  const ecommerceName = useAppSelector(
    (store) => store.settingsApi.settings.response.branding?.title
  )

  const costs = summary.costs

  const refundMethod =
    refundInfo.payment_method ??
    ReturnsTrackingInfoResponseRefundInfoStatusEnum.RefundNotNecessary
  const refundTiming =
    refundInfo.timing ??
    ReturnsTrackingInfoResponseRefundInfoStatusEnum.RefundNotNecessary

  const isRefunded =
    refundInfo.status ===
      ReturnsTrackingInfoResponseRefundInfoStatusEnum.FullyRefunded ||
    refundInfo.status ===
      ReturnsTrackingInfoResponseRefundInfoStatusEnum.PartiallyRefunded

  const giftCardCode = refundInfo.gift_card_code

  return (
    <section className="w-full rounded-lg border border-neutral-300/50 bg-white p-6 shadow-sm">
      <header className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-sm font-medium text-neutral-900">
          {t('tracking_new.refund_summary_title')}
        </h2>
        <RefundStatusBadge status={refundInfo.status} />
      </header>

      <dl className="flex flex-col gap-1.5">
        <div className="flex justify-between gap-2">
          <dt className="text-sm font-normal text-neutral-800">
            {t('tracking_new.items_subtotal')}
          </dt>
          <dd className="text-sm font-normal text-neutral-800">
            {summary.subtotal_formatted}
          </dd>
        </div>
        {costs?.map((cost) => {
          if (!cost.total) {
            return null
          }

          return (
            <div
              key={cost.subtype}
              className="flex items-center justify-between gap-2"
            >
              <dt className="text-sm font-normal text-neutral-800">
                {t(`cost_type.${cost.subtype}`, { ecommerceName })}
              </dt>
              <dd className="text-sm font-normal text-neutral-800">
                {cost.price_formatted}
              </dd>
            </div>
          )
        })}
        <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-neutral-200 pt-3">
          <dt className="text-sm font-medium text-neutral-800">
            {t('tracking_new.total_refund')}
          </dt>
          <dd className="text-base font-medium text-neutral-800">
            {refundInfo.status === 'NOT_REFUNDED'
              ? refundInfo.pending_formatted_amount
              : refundInfo.executed_formatted_amount}
          </dd>
        </div>
      </dl>
      {refundInfo.status === 'REFUND_NOT_NECESSARY' ? null : (
        <p className="pt-1.5 text-xs font-normal text-neutral-600">
          {isRefunded ? (
            <>
              {(() => {
                const date = getDate(refundInfo.refunded_at ?? '', i18n.language)
                return (
                  <Trans
                    i18nKey={
                      date
                        ? `tracking_refund.refunded_${refundMethod}`
                        : `tracking_refund.refunded_no_date_${refundMethod}`
                    }
                    values={{ date }}
                  />
                )
              })()}
              {(refundMethod === 'ORIGINAL' ||
                refundMethod === 'BANK_TRANSFER') && (
                <Tooltip
                  content={t('tracking_refund.refund_arrival_tooltip')}
                  classNames={{
                    content:
                      'max-w-52 text-xs text-center [text-wrap:pretty] py-2'
                  }}
                >
                  <span className="ml-1 inline-flex align-top">
                    <InfoCircleIcon className="size-3.5" />
                  </span>
                </Tooltip>
              )}
            </>
          ) : (
            <Trans
              i18nKey={`tracking_refund.pending_refund_${refundMethod}_${refundTiming}`}
            />
          )}
        </p>
      )}
      {giftCardCode && <GiftCardCode code={giftCardCode} />}
    </section>
  )
}

const GiftCardCode = ({ code }: { code: string }) => {
  const { t } = useTranslation()
  const posthog = usePostHog()
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    posthog?.capture('Gift Card Code Copied')
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-4 flex items-center gap-3 rounded-lg border border-solid border-neutral-300/50 px-3.5 py-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-800">
        <GiftIcon className="size-4" />
      </span>
      <div className="min-w-0 space-y-0.5">
        <p className="text-xs font-normal">
          {t('tracking_new.gift_card_code')}
        </p>
        <p className="truncate font-mono text-sm font-medium text-neutral-800">
          {code}
        </p>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className="ml-auto flex shrink-0 items-center gap-1 text-xs font-medium text-neutral-700 hover:text-neutral-900"
      >
        {copied ? (
          <CheckIcon className="size-4" strokeWidth={1.75} />
        ) : (
          <CopyIcon className="size-4" strokeWidth={1.75} />
        )}
        {copied ? t('tracking_new.copied') : t('tracking_new.copy')}
      </button>
    </div>
  )
}

const RefundStatusBadge = ({
  status
}: {
  status?: ReturnsTrackingInfoResponseRefundInfoStatusEnum
}) => {
  const { t } = useTranslation()

  switch (status) {
    case ReturnsTrackingInfoResponseRefundInfoStatusEnum.NotRefunded:
      return (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-[#4F4700]">
          <span className="size-1.5 rounded-full bg-current" />
          {t('tracking_new.pending')}
        </span>
      )
    default:
      return null
  }
}
